// The HMRC trust-score step shared by the validation card (after Next) and the review card: which stage a card is at, the
// score step's state machine, and the cards' fixed wording. Pure, so the unit tests import it directly.

export type HmrcCardKind = 'validation' | 'review';

// 'sign' is the validation card's Next (hmrc/add-company validates the registrar entry); its Complete records the ledger
// message once the score step has answered
export type HmrcStage = 'not-validated' | 'sign' | 'vat-name-pending' | 'vat-name-rejected' | 'score' | 'done';

export type VatNameReviewStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

// The validation job's VAT_NAME_REVIEW metadata (hmrc/auth/complete writes it, hmrc/vat-name-review decides it)
export interface VatNameReview {
  status: VatNameReviewStatus;
  companyName: string;
  vatName: string;
  vrn: string;
  requestedAt: string;
  // When support decided it; who decided and their notes stay on support's own card
  reviewedAt?: string | null;
}

export interface StageInputs {
  kind: HmrcCardKind;
  jobComplete: boolean;
  // The company's HMRC registrar entry is validated for this card's VAT number (pastNext)
  registrarValidated: boolean;
  // HMRC confirmed the business on this card (VERIFIED_BUSINESS)
  businessVerified: boolean;
  vatNameReview: VatNameReview | null;
}

/**
 * Where a card is. A review card is at its score step until it completes. A validation card is at its score step once
 * its registrar entry is validated (Next ran, or the company was already validated for this VAT number); before that, a
 * pending or rejected VAT-name review takes Next's place. Its Complete then records the ledger message, whatever the
 * score said.
 */
export function deriveStage(inputs: StageInputs): HmrcStage {
  if (inputs.jobComplete) return 'done';
  if (inputs.kind === 'review') return 'score';
  if (inputs.registrarValidated) return 'score';
  if (inputs.vatNameReview?.status === 'PENDING') return 'vat-name-pending';
  if (inputs.vatNameReview?.status === 'REJECTED') return 'vat-name-rejected';
  if (inputs.businessVerified) return 'sign';
  return 'not-validated';
}

export interface NextInputs {
  // This validation workflow has its public ledger message (HMRC_PL_TRANSACTION): Complete recorded it on this card
  ledgerRecorded: boolean;
  // The company's HMRC registrar entry, as the card read it
  entryValidated: boolean;
  entryProvenId: string | null | undefined;
  // The grant hmrc/auth/complete parked on the job for Next (PROVEN_VRN), and the VAT number HMRC confirmed on it
  parkedVrn: string | null | undefined;
  verifiedVrn: string | null | undefined;
  // HMRC's not-MTD answer on this job (HMRC_NOT_MTD): once confirmed at the score step it unverifies the entry for that VRN
  notMtdVrn?: string | null;
}

/**
 * Whether a validation card is past Next. Either the entry is validated for the VAT number HMRC confirmed on this card
 * with nothing parked: Next moved the grant onto it, or the company was already validated for this VAT number, in which
 * case hmrc/auth/complete stores the new tokens straight on the entry and parks nothing for Next to move. Or its ledger
 * message is already recorded. An entry unverified only by this card's own confirmed not-MTD answer for its VRN (the
 * score step's, after Next) still counts, as hmrc/record-validation accepts it: Complete records the validation whatever
 * the score said.
 */
export function pastNext(inputs: NextInputs): boolean {
  if (inputs.ledgerRecorded) return true;
  const provenId = inputs.entryProvenId;
  if (inputs.parkedVrn || !provenId || inputs.verifiedVrn !== provenId) return false;
  return inputs.entryValidated || inputs.notMtdVrn === provenId;
}

/**
 * The validation job's outcome. `complete` waits for the ledger message Complete (hmrc/record-validation) queued; a card
 * that recorded none (no VAT, or not signed up for MTD) finishes with `skip`, or its workflow would wait for that message
 * for ever.
 */
export function validationOutcome(ledgerRecorded: boolean): 'complete' | 'skip' {
  return ledgerRecorded ? 'complete' : 'skip';
}

const REVIEW_STATUSES: readonly string[] = ['PENDING', 'APPROVED', 'REJECTED'];

/** The job's VAT_NAME_REVIEW metadata value, or null when it is blank, unreadable or not a review. */
export function parseVatNameReview(value: string | null | undefined): VatNameReview | null {
  if (!value) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const review = parsed as Partial<VatNameReview>;
    if (typeof review.status !== 'string' || !REVIEW_STATUSES.includes(review.status)) return null;
    return {
      status: review.status,
      companyName: typeof review.companyName === 'string' ? review.companyName : '',
      vatName: typeof review.vatName === 'string' ? review.vatName : '',
      vrn: typeof review.vrn === 'string' ? review.vrn : '',
      requestedAt: typeof review.requestedAt === 'string' ? review.requestedAt : '',
      reviewedAt: review.reviewedAt ?? null,
    };
  } catch {
    return null;
  }
}

// The score step

export type ScorePhase = 'idle' | 'running' | 'done' | 'retry' | 'countdown' | 'reconnect' | 'not-eligible';

export interface ScoreState {
  phase: ScorePhase;
  // The lambda's fixed sentence (never HMRC's text), or the card's own when the call failed
  message: string | null;
  // Seconds left before Retry comes back, while phase is countdown
  secondsLeft: number;
}

// What hmrc/trust-score answers inside its HTTP 200: code first, then status
export interface ScoreAnswer {
  code?: unknown;
  status?: unknown;
  retryAfterSeconds?: unknown;
  message?: unknown;
}

export type ScoreEvent =
  | { type: 'run' }
  // declared: the run stored the user's "not signed up for MTD", which the lambda refuses unless HMRC said so first
  | { type: 'answer'; answer: ScoreAnswer; declared?: boolean }
  | { type: 'failed'; message?: string | null; declared?: boolean }
  | { type: 'tick' };

export const INITIAL_SCORE_STATE: ScoreState = { phase: 'idle', message: null, secondsLeft: 0 };

// The lambda's own wait when HMRC is busy, used when an answer leaves it out
export const DEFAULT_RETRY_AFTER_SECONDS = 10;

const messageOf = (answer: ScoreAnswer, fallback: string) =>
  typeof answer.message === 'string' && answer.message ? answer.message : fallback;

function answered(answer: ScoreAnswer, declared = false): ScoreState {
  // A declaration that is refused or fails leaves the user at the two possibilities, so trying again declares again
  // rather than running a score HMRC has just refused
  if (declared && !(answer.code === 200 && answer.status === 'OK')) {
    const failed = answer.code === 200 && answer.status === 'NOT_ELIGIBLE' ? WORDING.notEligible : WORDING.failed;
    return { phase: 'reconnect', message: messageOf(answer, failed), secondsLeft: 0 };
  }
  if (answer.code !== 200) return { phase: 'retry', message: messageOf(answer, WORDING.failed), secondsLeft: 0 };
  switch (answer.status) {
    case 'OK':
      return { phase: 'done', message: messageOf(answer, WORDING.done), secondsLeft: 0 };
    case 'RETRY':
      return { phase: 'retry', message: messageOf(answer, WORDING.failed), secondsLeft: 0 };
    case 'TRY_AGAIN_IN': {
      const wait = answer.retryAfterSeconds;
      const seconds =
        typeof wait === 'number' && Number.isFinite(wait) && wait > 0 ? Math.ceil(wait) : DEFAULT_RETRY_AFTER_SECONDS;
      return { phase: 'countdown', message: messageOf(answer, WORDING.busy), secondsLeft: seconds };
    }
    case 'RECONNECT':
      return { phase: 'reconnect', message: messageOf(answer, WORDING.reconnect), secondsLeft: 0 };
    case 'NOT_ELIGIBLE':
      return { phase: 'not-eligible', message: messageOf(answer, WORDING.notEligible), secondsLeft: 0 };
    default:
      // An answer this card does not know: worth trying again, never a success
      return { phase: 'retry', message: WORDING.failed, secondsLeft: 0 };
  }
}

/** The score step's next state. A run starts only from a state that offers Run or Retry; done is final. */
export function reduce(state: ScoreState, event: ScoreEvent): ScoreState {
  switch (event.type) {
    case 'run':
      if (state.phase === 'idle' || state.phase === 'retry' || state.phase === 'reconnect') {
        return { phase: 'running', message: null, secondsLeft: 0 };
      }
      return state;
    case 'answer':
      return state.phase === 'running' ? answered(event.answer, event.declared) : state;
    case 'failed':
      if (state.phase !== 'running') return state;
      return {
        phase: event.declared ? 'reconnect' : 'retry',
        message: event.message || WORDING.failed,
        secondsLeft: 0,
      };
    case 'tick':
      if (state.phase !== 'countdown') return state;
      return state.secondsLeft > 1
        ? { ...state, secondsLeft: state.secondsLeft - 1 }
        : { phase: 'retry', message: state.message, secondsLeft: 0 };
    default:
      return state;
  }
}

/** Whether the step offers Run (idle) or Retry (retry, or a countdown still running down) in the card's button bar. */
export function scoreAction(state: ScoreState): { label: 'Run' | 'Retry'; disabled: boolean; seconds: number } | null {
  if (state.phase === 'idle') return { label: 'Run', disabled: false, seconds: 0 };
  if (state.phase === 'retry') return { label: 'Retry', disabled: false, seconds: 0 };
  if (state.phase === 'countdown') return { label: 'Retry', disabled: true, seconds: state.secondsLeft };
  return null;
}

/**
 * Whether the card offers Complete: once a run has answered, whatever it said, and on the two possibilities. The user may
 * close the job without an HMRC score; only while nothing has been run, or a run is out, is there nothing to complete.
 */
export function canComplete(state: ScoreState): boolean {
  return state.phase !== 'idle' && state.phase !== 'running';
}

/** Whether the step's answer is a problem the user is told about as one (an alert), rather than a success (a status). */
export function isScoreProblem(state: ScoreState): boolean {
  return state.phase === 'retry' || state.phase === 'countdown' || state.phase === 'not-eligible';
}

// The cards' fixed wording

export const WORDING = {
  consentHeading: 'Run your HMRC trust score',
  consent:
    "nodeStream will now read your VAT returns, payments, penalties and registration from HMRC to calculate the HMRC part of your company's trust score. This takes a few seconds and only reads your records; nothing is changed at HMRC.",
  running: 'Calculating your HMRC trust score…',
  done: 'Trust score updated.',
  doneNext: 'You can now complete this job.',
  completeWithout: 'You can try again, or complete this job without an HMRC trust score.',
  completeWithoutReview:
    "You can try again, or complete this card without re-running it; the HMRC part of your company's trust score falls to 0 at the deadline above.",
  failed: "We couldn't calculate your HMRC trust score. Please try again.",
  busy: 'HMRC is busy, please try again shortly.',
  reconnect: 'Your HMRC connection needs renewing.',
  notEligible: 'This HMRC trust score check cannot be run for this job.',
  reconnectHeading: 'HMRC did not let us read this VAT number',
  reconnectOptions:
    "There are two possibilities. Either the HMRC connection needs renewing, or it was made with an HMRC account that does not hold this VAT number: reconnect with the HMRC account that holds it. Or the business is not signed up for Making Tax Digital for VAT: tell us, and your company's HMRC score will be 0.",
  notMtdConfirmTitle: 'Not signed up for Making Tax Digital?',
  notMtdConfirm:
    "Your company's HMRC trust score will be 0, and its HMRC connection will be removed, so it is no longer verified with HMRC. You can connect HMRC again once the business is signed up for Making Tax Digital for VAT.",
  notMtdConfirmAction: 'Confirm',
  notRegisteredFailed: "We couldn't record that your company is not VAT registered. Please try again.",
  review:
    "Your company's HMRC trust score is due for its regular review. Run it again with HMRC so your published score stays up to date.",
  reviewDeadlinePrefix: 'Complete this review within 14 days of this card being created, by',
  reviewDeadlineSuffix: "or the HMRC part of your company's trust score falls to 0.",
  reviewNotAllowed: 'Only the person this review is assigned to, or a director, can re-score this company.',
  vatNamePending: 'nodeStream support is reviewing your VAT registration name.',
  vatNamePendingDetail:
    'Your VAT registration name does not match your company name, so nodeStream support will check your VAT number before you can continue. This card updates as soon as they decide.',
  vatNameRejected:
    'nodeStream support did not approve this VAT number for your company, as its VAT registration name does not match your company name.',
  vatNameRejectedAction: 'Connect HMRC again with the HMRC account of the business whose VAT number this is.',
} as const;

// The review card's deadline: 14 days from the card's creation (exact unless the card was reassigned, which restarts it)
export const REVIEW_DAYS = 14;

/** When a review card created at `createdAt` expires, or null for an unreadable date. */
export function reviewDeadline(createdAt: string | null | undefined): Date | null {
  if (!createdAt) return null;
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return null;
  return new Date(created.getTime() + REVIEW_DAYS * 24 * 60 * 60 * 1000);
}

// Who may re-score from the review card, as hmrc/trust-score checks it: the card's assignee, or a verified director, with
// an employed profile in the job's company (a profile without `employed` counts as employed)
export interface Viewer {
  id: string;
  companyId: string;
  director?: string | null;
  employed?: boolean | null;
}

export function canRescore(
  viewer: Viewer | null | undefined,
  job: { assignedTo?: string | null; companyId?: string | null },
): boolean {
  if (!viewer || !job.companyId || viewer.companyId !== job.companyId) return false;
  if (viewer.employed === false) return false;
  return viewer.id === job.assignedTo || viewer.director === 'VERIFIED';
}

// The support VAT-name card

export type VatNameDecision = 'APPROVE' | 'REJECT';

// The review a support card is about (its job's vatNameReview metadata): the review's identity, without status or decision
export type VatNameSnapshot = Pick<VatNameReview, 'vrn' | 'companyName' | 'vatName' | 'requestedAt'>;

/** The support job's vatNameReview snapshot, or null when it is blank or unreadable (the lambda reads it the same way). */
export function parseVatNameSnapshot(value: string | null | undefined): VatNameSnapshot | null {
  if (!value) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { vrn, companyName, vatName, requestedAt } = parsed as Record<string, unknown>;
    if (
      typeof vrn !== 'string' ||
      typeof companyName !== 'string' ||
      typeof vatName !== 'string' ||
      typeof requestedAt !== 'string'
    ) {
      return null;
    }
    return { vrn, companyName, vatName, requestedAt };
  } catch {
    return null;
  }
}

// What hmrc/vat-name-review answers: { code: 200, status } or a refusal { code, message, reason }
export interface VatNameDecisionAnswer {
  code?: unknown;
  message?: unknown;
  reason?: unknown;
  status?: unknown;
}

export type VatNameDecisionResult =
  | { kind: 'decided'; message: string }
  | { kind: 'closed'; message: string }
  | { kind: 'refused'; message: string; reload: boolean };

export const VAT_NAME_REFUSALS: Record<string, string> = {
  FORBIDDEN: 'Only nodeStream support or an administrator can review a VAT registration name.',
  NOT_ASSIGNEE: 'This review is assigned to someone else.',
  NOT_FOUND: 'This VAT registration name review no longer exists.',
  ALREADY_DECIDED: 'This review has already been decided.',
  NO_PROFILE: 'Your nodeStream profile could not be found. Switch to the nodeStream company and try again.',
  STALE_REVIEW:
    'The VAT registration name review has changed since this card showed it. The card now shows the latest review: check it and decide again.',
  COMPANY_MISMATCH: "This review is not about that company's validation card. Contact the nodeStream team.",
};

/** What the support card says after hmrc/vat-name-review answered. A stale review makes the card read its job again. */
export function vatNameDecisionResult(answer: VatNameDecisionAnswer): VatNameDecisionResult {
  if (answer.code === 200) {
    if (answer.status === 'APPROVED') return { kind: 'decided', message: 'VAT number approved.' };
    if (answer.status === 'REJECTED') return { kind: 'decided', message: 'VAT number rejected.' };
    return {
      kind: 'closed',
      message: 'The validation card no longer waits on this review, so this card has been closed.',
    };
  }
  const reason = typeof answer.reason === 'string' ? answer.reason : '';
  const known = VAT_NAME_REFUSALS[reason];
  const message =
    known ??
    (typeof answer.message === 'string' && answer.message ? answer.message : 'The decision could not be saved.');
  return { kind: 'refused', message, reload: reason === 'STALE_REVIEW' };
}
